import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { Badge, Button } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { writeActionFor } from '@rbac/writeAction';

/**
 * The request log — every call the mock actually received, matched or not.
 *
 * The unmatched rows are the point: "my code called the mock and got a 404" is the
 * most common way a mock goes wrong, and the only way to debug it is to see the exact
 * path and body that arrived versus the routes that exist.
 */
const OUTCOME_TONE = { matched: 'success', unmatched: 'error', chaos: 'warning', proxied: 'info', recorded: 'info' };

function Row({ row, expanded, onToggle }) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-[4rem_1fr_3.5rem_6rem_auto] items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-[var(--app-border)]/20"
      >
        <span className="font-mono font-medium">{row.method}</span>
        <span className="font-mono truncate" title={row.path}>{row.path}</span>
        <Badge variant={row.response_status >= 400 ? 'error' : 'success'} size="sm">{row.response_status}</Badge>
        <Badge variant={OUTCOME_TONE[row.outcome] || 'secondary'} size="sm">{row.outcome}</Badge>
        <span className="text-secondary-500 whitespace-nowrap">{new Date(row.created_at).toLocaleTimeString()}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-2 space-y-1 text-xs">
          {row.body && (
            <div>
              <div className="text-secondary-500">Request body</div>
              <pre className="font-mono whitespace-pre-wrap break-all max-h-40 overflow-auto">{row.body}</pre>
            </div>
          )}
          <div>
            <div className="text-secondary-500">Response body</div>
            <pre className="font-mono whitespace-pre-wrap break-all max-h-40 overflow-auto">{row.response_body}</pre>
          </div>
          <div className="text-secondary-500">
            {row.duration_ms}ms · scenario {row.scenario || 'default'} · {row.ip || 'no ip'}
          </div>
        </div>
      )}
    </>
  );
}

export default function RequestLogPanel({ environmentId }) {
  const { fetchApi } = useAppApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyUnmatched, setOnlyUnmatched] = useState(false);
  const [openId, setOpenId] = useState(null);

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('mockRequests', { id: environmentId, limit: 100, matched: onlyUnmatched ? false : undefined })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [environmentId, onlyUnmatched]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" onClick={reload} disabled={loading}>
          <RefreshCw size={13} /> Refresh
        </Button>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={onlyUnmatched} onChange={(e) => setOnlyUnmatched(e.target.checked)} />
          Only unmatched
        </label>
        <span className="flex-1" />
        <Can module="mockapi" action={writeActionFor('mockapi')}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => fetchApi('mockRequestsClear', environmentId).then(reload)}
          >
            <Trash2 size={13} /> Clear
          </Button>
        </Can>
      </div>

      <div className="rounded-lg border border-[var(--app-border)] divide-y divide-[var(--app-border)]">
        {rows.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-secondary-500">
            {loading ? 'Loading…' : 'Nothing has called this mock yet.'}
          </div>
        )}
        {rows.map((row) => (
          <Row
            key={row.id}
            row={row}
            expanded={openId === row.id}
            onToggle={() => setOpenId((id) => (id === row.id ? null : row.id))}
          />
        ))}
      </div>
    </div>
  );
}
